import React, { useState, useEffect } from "react";
import { convertToArabicNumbers, dapatkanRingkasanJuz } from "@/helpers";
import { Search, BookOpen, HelpCircle, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import ThemeToggle from "@/components/ThemeToggle";
import MobileHeader from "@/components/MobileHeader";

export default function JuzList() {
  const allJuz = dapatkanRingkasanJuz();
  const [juzList, setJuzList] = useState(allJuz);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const searchJuz = (event) => {
    const searchTerm = event.target.value.toLowerCase();
    const filtered = allJuz.filter((juz) => {
      return (
        juz.juz.toString().includes(searchTerm) ||
        juz.surahAwal.nama.toLowerCase().includes(searchTerm) ||
        juz.surahAkhir.nama.toLowerCase().includes(searchTerm)
      );
    });
    setJuzList(filtered);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 transition-colors duration-300">
      <SEO
        title="Daftar 30 Juz Al-Qur'an"
        description="Jelajahi Al-Qur'an berdasarkan 30 Juz. Baca dan pelajari setiap Juz dengan terjemahan Bahasa Indonesia."
      />

      {/* Mobile Header */}
      <MobileHeader title="Baca per Juz" />

      {/* Desktop Hero */}
      <div className="hidden md:block relative overflow-hidden bg-[#0a2e26] dark:bg-slate-950 pt-20 pb-16 sm:py-24 px-4 sm:px-6">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/islamic-art.png')]"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-emerald-950/50 to-[#0a2e26] dark:to-slate-950"></div>
        </div>

        <div className="absolute top-6 right-6 z-50 flex items-center gap-2">
          <ThemeToggle />
          <Link
            to="/bantuan"
            className="p-2.5 bg-white/10 hover:bg-white/20 rounded-2xl backdrop-blur-md text-white transition-all active:scale-95 group border border-white/10"
            title="Pusat Bantuan"
          >
            <HelpCircle className="w-5 h-5 group-hover:rotate-12 transition-transform" />
          </Link>
        </div>

        <div className="relative max-w-4xl mx-auto text-center space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-500/20 rounded-full border border-emerald-500/20 text-emerald-400 text-[10px] font-black uppercase tracking-[0.2em] animate-fade-in">
            30 Juz Al-Qur'an
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-7xl font-black text-white tracking-tight animate-slide-up leading-[1.1]">
            Baca per <span className="text-emerald-400">Juz</span>
          </h1>

          <p className="text-lg text-emerald-100/60 max-w-xl mx-auto leading-relaxed animate-fade-in delay-100 px-4">
            Jelajahi Al-Qur'an berdasarkan pembagian 30 Juz untuk kemudahan
            membaca dan menghafal.
          </p>

          <div className="flex items-center justify-center gap-3 pt-4 animate-fade-in delay-200">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-2xl font-bold transition-all border border-white/10 active:scale-95"
            >
              <BookOpen size={18} />
              Baca per Surah
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 md:py-12 content-with-bottom-nav md:pb-0">
        <div className="mb-12">
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
              <Search
                className="h-5 w-5 text-gray-400 group-focus-within:text-emerald-500 transition-colors"
                strokeWidth={2.5}
              />
            </div>
            <input
              onChange={searchJuz}
              type="search"
              className="w-full pl-14 pr-6 py-5 text-lg border border-gray-100 dark:border-slate-800 rounded-3xl bg-white dark:bg-slate-900 shadow-2xl shadow-emerald-500/5 focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 focus:outline-none transition-all duration-300 dark:text-white dark:placeholder-gray-500"
              placeholder="Cari Juz atau Surah..."
            />
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-[0_8px_30px_rgba(0,0,0,0.1)] md:bg-transparent md:shadow-none md:dark:bg-transparent grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 md:gap-6 overflow-hidden md:overflow-visible border border-gray-100 dark:border-slate-700 md:border-none">
          {juzList.length === 0 ? (
            <div className="col-span-full text-center py-16">
              <BookOpen
                className="w-12 h-12 text-gray-400 mx-auto mb-6"
                strokeWidth={1.5}
              />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Tidak ada data ditemukan
              </h3>
            </div>
          ) : (
            juzList.map((juz, index) => (
              <Link
                key={index}
                to={`/juz/${juz.juz}`}
                className={`group flex md:flex-col h-full bg-white dark:bg-slate-800 md:rounded-2xl md:shadow-[0_2px_10px_rgba(0,0,0,0.02)] md:hover:shadow-xl dark:md:hover:shadow-emerald-900/10 transition-all duration-300 md:overflow-hidden md:border border-gray-100 dark:border-slate-700 md:hover:border-emerald-200 dark:md:hover:border-emerald-800 items-center md:items-stretch p-4 md:p-0 active:bg-gray-50 dark:active:bg-slate-700/50 md:active:bg-white ${index !== juzList.length - 1 ? 'border-b border-gray-50 dark:border-slate-700/50 md:border-b-gray-100' : ''}`}
              >
                {/* Icon */}
                <div className="p-0 md:p-6 md:pb-0 flex-shrink-0">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-emerald-500 to-blue-500 dark:from-emerald-600 dark:to-emerald-800 rounded-full flex items-center justify-center text-white font-bold md:text-xl shadow-md group-hover:scale-105 transition-transform duration-200">
                    {convertToArabicNumbers(juz.juz)}
                  </div>
                </div>

                {/* Content */}
                <div className="ml-4 md:ml-0 md:p-6 flex-1 min-w-0 flex flex-col justify-center md:justify-start">
                  <h3 className="text-base md:text-2xl font-bold text-gray-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors duration-200 mb-0.5 md:mb-3">
                    Juz {juz.juz}
                  </h3>

                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate md:whitespace-normal md:space-y-2 md:text-sm">
                    <span className="md:hidden">{juz.surahAwal.nama} — {juz.surahAkhir.nama}</span>
                    <div className="hidden md:flex items-center justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Mulai dari:</span>
                      <span className="font-semibold text-gray-700 dark:text-gray-200">{juz.surahAwal.nama} : {juz.surahAwal.ayat}</span>
                    </div>
                    <div className="hidden md:flex items-center justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Berakhir di:</span>
                      <span className="font-semibold text-gray-700 dark:text-gray-200">{juz.surahAkhir.nama} : {juz.surahAkhir.ayat}</span>
                    </div>
                  </div>
                </div>

                {/* Mobile Right Actions */}
                <div className="md:hidden flex items-center ml-2 flex-shrink-0">
                  <ArrowRight size={18} className="text-gray-300 dark:text-gray-600 group-hover:text-emerald-500 transition-colors" />
                </div>

                {/* Desktop Footer */}
                <div className="hidden md:flex px-6 py-4 bg-gray-50 dark:bg-slate-800/50 border-t border-gray-100 dark:border-slate-700 mt-auto items-center justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">
                    {juz.jumlahSurah} Surah
                  </span>
                  <span className="text-emerald-600 dark:text-emerald-400 font-semibold group-hover:translate-x-1 transition-transform duration-200">
                    Baca Sekarang →
                  </span>
                </div>
              </Link>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
